
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { localizePath, stripLocalePrefix } from '@/lib/localeRoutes';
import { usePostHog } from '@/analytics/usePostHog';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' }
];

export const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const analytics = usePostHog();

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = async (languageCode: string) => {
    const capture = analytics?.captureScope();
    setIsOpen(false);
    try {
      await i18n.changeLanguage(languageCode);
    } catch {
      // Resource load failure: keep the current language. The router-level
      // <LocaleRoute> would re-sync on next navigation, but failing silently
      // here would leave the dropdown reflecting an unsaved selection.
      return;
    }
    const basePath = stripLocalePrefix(location.pathname);
    const target = localizePath(basePath, languageCode);
    navigate(target + location.search + location.hash);
    capture?.('language_changed', { locale: languageCode });
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-11 min-w-11 gap-2">
          <span>{currentLanguage.code.toUpperCase()}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`min-h-11 cursor-pointer ${
              i18n.language === language.code ? 'bg-muted' : ''
            }`}
          >
            <div className="flex items-center justify-between w-full">
              <span>{language.nativeName}</span>
              <span className="text-xs text-muted-foreground uppercase">{language.code}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
